// AM Advertising Home — the book table's row model + sorting. PURE.
//
// Money stays per-currency (CurrencyAmount[]) all the way here; the
// display-currency conversion happens at the RENDER edge via the existing
// machinery (formatDisplayMoney / toDisplayCurrency), exactly as on the two
// reporting dashboards. Blended CPL is combined spend ÷ combined leads — but
// only PER CURRENCY here; a cross-currency blend exists solely through the rate
// table at the display edge.

import type {
  HomeAccount,
  HomeAccountHealth,
  MetaLeadBreakdown,
  SpineDay,
} from '../../types/advertisingHome.js';
import type { CurrencyAmount } from '../../types/reporting.js';
import type { ChannelPacing } from './alerts.js';
import { BrandAverages, classifyCpl, type CplVerdict } from './noLeads.js';

export interface BookChannelCell {
  /** False = the account doesn't run this channel → render "—", never 0. */
  active: boolean;
  spend: CurrencyAmount[];
  leads: number;
  /** Per-currency CPL (spend_c ÷ leads_c); null when nothing computable. */
  cpl: CurrencyAmount[] | null;
  /**
   * THE ONE RULE (noLeads.ts) for this channel over the range, in its dominant
   * currency. `no_leads` = spending with no leads: render "No leads · $X spent",
   * never "—" or $0, and sort it WORSE than any finite CPL.
   */
  verdict: CplVerdict;
  /** The currency the verdict was judged in (null when nothing spent). */
  verdictCurrency: string | null;
}

/**
 * §11d — the Meta lead-ACTION detail for one account.
 *
 * ⚠️⚠️ A DIFFERENT AXIS FROM THE CAMPAIGN-SETUP SPLIT (§11e), and they will
 * disagree if conflated: this is which MECHANISM recorded the lead
 * (`onsite_conversion.lead_grouped` vs the pixel), NOT how the campaign was
 * configured. A website-lead campaign can still record an on-Meta lead action.
 *
 * ⚠️⚠️ `costPerMetaLead` DIVIDES THE SCOPED NUMERATOR. Its spend is
 * `metaLeadSpend` — spend on on-Meta instant-form lead campaigns only — and NOT
 * the account's total Meta spend, which would charge instant-form leads for
 * traffic and engagement budget and inflate the figure. That is the §4b error
 * §10 recorded; the two amounts are separate fields so a call site cannot pick
 * the wrong one by accident.
 */
export interface MetaLeadCell {
  metaLeads: number;
  /** The scoped numerator, per currency — shown so the ratio is auditable. */
  metaLeadSpend: CurrencyAmount[];
  /** Per-currency cost per Meta lead; null where it cannot be computed. */
  costPerMetaLead: CurrencyAmount[] | null;
}

export interface BookRow {
  account: HomeAccount;
  meta: BookChannelCell;
  google: BookChannelCell;
  blendedSpend: CurrencyAmount[];
  blendedLeads: number;
  blendedCpl: CurrencyAmount[] | null;
  /** Blended verdict: `no_leads` when blended leads are 0 and a channel is spending-no-leads. */
  blendedVerdict: CplVerdict;
  /** Raw current/previous pairs — the client renders deltas via computeDelta. */
  leadsPrevious: number;
  /** Dominant-currency blended CPL, current + previous (for the trend delta). */
  cplCurrent: number | null;
  cplPrevious: number | null;
  /** (cplCurrent − cplPrevious) / cplPrevious × 100; null when incomparable. */
  cplChangePct: number | null;
  /** Daily leads over the selected range (the sparkline). */
  spark: Array<{ date: string; leads: number; spend: number }>;
  pacingMeta: ChannelPacing | null;
  pacingGoogle: ChannelPacing | null;
  health: HomeAccountHealth;
  /**
   * §11d — null when the caller supplied no breakdown (the AM board, and the
   * Google board) OR when this account has no Meta rows in the range. ⚠️ Null
   * renders "—", never 0: an account we have no Meta figures for has not
   * produced zero Meta leads.
   */
  metaLead: MetaLeadCell | null;
}

const r2 = (n: number): number => Math.round(n * 100) / 100;

interface Fold {
  spendBy: Map<string, number>;
  leadsBy: Map<string, number>;
  leads: number;
}

const emptyFold = (): Fold => ({ spendBy: new Map(), leadsBy: new Map(), leads: 0 });

function foldRows(rows: readonly SpineDay[]): Fold {
  const f = emptyFold();
  for (const r of rows) {
    f.spendBy.set(r.currency, (f.spendBy.get(r.currency) ?? 0) + r.spend);
    f.leadsBy.set(r.currency, (f.leadsBy.get(r.currency) ?? 0) + r.leads);
    f.leads += r.leads;
  }
  return f;
}

function toAmounts(m: Map<string, number>): CurrencyAmount[] {
  return [...m.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([currency, amount]) => ({ currency, amount: r2(amount) }));
}

/** Per-currency CPL from a fold — a currency with no leads yields no entry. */
function cplAmounts(f: Fold): CurrencyAmount[] | null {
  const out: CurrencyAmount[] = [];
  for (const [currency, spend] of f.spendBy) {
    const leads = f.leadsBy.get(currency) ?? 0;
    if (leads > 0 && spend > 0) out.push({ currency, amount: r2(spend / leads) });
  }
  out.sort((a, b) => a.currency.localeCompare(b.currency));
  return out.length > 0 ? out : null;
}

function dominant(f: Fold): string | null {
  let best: string | null = null;
  let max = -Infinity;
  for (const [c, amount] of f.spendBy) {
    if (amount > max) {
      max = amount;
      best = c;
    }
  }
  return best;
}

function cplIn(f: Fold, currency: string | null): number | null {
  if (!currency) return null;
  const spend = f.spendBy.get(currency) ?? 0;
  const leads = f.leadsBy.get(currency) ?? 0;
  return spend > 0 && leads > 0 ? spend / leads : null;
}

/**
 * §11d — fold an account's per-currency breakdown rows into one cell.
 *
 * ⚠️ MONEY STAYS PER CURRENCY, like everything else here — a cross-currency
 * blend exists only through the rate table at the display edge.
 * ⚠️ A currency with spend but NO Meta leads contributes no CPL entry rather
 * than a zero: money spent for nothing is not a cost per lead of $0.
 */
export function buildMetaLeadCell(
  rows: readonly MetaLeadBreakdown[] | undefined,
): MetaLeadCell | null {
  if (!rows || rows.length === 0) return null;
  const spendBy = new Map<string, number>();
  const leadsBy = new Map<string, number>();
  let metaLeads = 0;
  for (const r of rows) {
    spendBy.set(r.currency, (spendBy.get(r.currency) ?? 0) + r.metaLeadSpend);
    leadsBy.set(r.currency, (leadsBy.get(r.currency) ?? 0) + r.metaLeads);
    metaLeads += r.metaLeads;
  }
  const cpl: CurrencyAmount[] = [];
  for (const [currency, spend] of spendBy) {
    const leads = leadsBy.get(currency) ?? 0;
    if (leads > 0 && spend > 0) cpl.push({ currency, amount: r2(spend / leads) });
  }
  cpl.sort((a, b) => a.currency.localeCompare(b.currency));
  return {
    metaLeads: r2(metaLeads),
    metaLeadSpend: toAmounts(spendBy),
    costPerMetaLead: cpl.length > 0 ? cpl : null,
  };
}

export function buildBookRows(input: {
  accounts: readonly HomeAccount[];
  rangeRows: readonly SpineDay[];
  compareRows: readonly SpineDay[];
  healthByAccount: ReadonlyMap<string, HomeAccountHealth>;
  pacingByAccount?: ReadonlyMap<string, { meta: ChannelPacing | null; google: ChannelPacing | null }>;
  /**
   * §11d — the Meta lead breakdown, one entry per (account, currency).
   *
   * ⚠️ OPTIONAL, and ABSENT EVERYWHERE BUT THE META BOARD. Omitting it leaves
   * `row.metaLead` null and every existing caller byte-identical, which is what
   * keeps Advertising Home out of scope.
   */
  metaLeadsByAccount?: ReadonlyMap<string, readonly MetaLeadBreakdown[]>;
  /**
   * Brand averages for THIS table's range (all accounts in each brand). Absent =
   * every threshold is the fallback. See noLeads.ts.
   */
  brandAverages?: BrandAverages;
}): BookRow[] {
  const brandAverages = input.brandAverages ?? new BrandAverages();
  const byAccountChannel = new Map<string, SpineDay[]>();
  const sparkByAccount = new Map<string, Map<string, { leads: number; spend: number }>>();
  for (const row of input.rangeRows) {
    const key = `${row.accountId}|${row.channel}`;
    const list = byAccountChannel.get(key);
    if (list) list.push(row);
    else byAccountChannel.set(key, [row]);
    if (row.date) {
      let days = sparkByAccount.get(row.accountId);
      if (!days) {
        days = new Map();
        sparkByAccount.set(row.accountId, days);
      }
      const d = days.get(row.date) ?? { leads: 0, spend: 0 };
      d.leads += row.leads;
      d.spend += row.spend;
      days.set(row.date, d);
    }
  }
  const compareByAccount = new Map<string, SpineDay[]>();
  for (const row of input.compareRows) {
    const list = compareByAccount.get(row.accountId);
    if (list) list.push(row);
    else compareByAccount.set(row.accountId, [row]);
  }

  return input.accounts.map((account) => {
    const metaRows = byAccountChannel.get(`${account.id}|meta`) ?? [];
    const googleRows = byAccountChannel.get(`${account.id}|google`) ?? [];
    const metaFold = foldRows(metaRows);
    const googleFold = foldRows(googleRows);
    const blendedFold = foldRows([...metaRows, ...googleRows]);
    const prevFold = foldRows(compareByAccount.get(account.id) ?? []);

    const cell = (active: boolean, f: Fold, channel: 'meta' | 'google'): BookChannelCell => {
      const cur = dominant(f);
      const verdict: CplVerdict = cur
        ? classifyCpl({
            spend: f.spendBy.get(cur) ?? 0,
            leads: f.leadsBy.get(cur) ?? 0,
            brandCpl: brandAverages.cpl(account.brandAccountId, channel, cur),
          })
        : { kind: 'none' };
      return {
        active,
        spend: toAmounts(f.spendBy),
        leads: r2(f.leads),
        cpl: cplAmounts(f),
        verdict,
        verdictCurrency: cur,
      };
    };
    const metaCell = cell(account.runsMeta, metaFold, 'meta');
    const googleCell = cell(account.runsGoogle, googleFold, 'google');
    const flagged = [metaCell, googleCell].filter((c) => c.verdict.kind === 'no_leads');
    const blendedVerdict: CplVerdict =
      blendedFold.leads <= 0 && flagged.length > 0
        ? {
            kind: 'no_leads',
            spend: flagged.reduce(
              (n, c) => n + (c.verdict.kind === 'no_leads' ? c.verdict.spend : 0),
              0,
            ),
            threshold: 0,
            usedFallback: false,
          }
        : cplAmounts(blendedFold) !== null
          ? { kind: 'cpl', cpl: (cplIn(blendedFold, dominant(blendedFold)) ?? 0) }
          : { kind: 'none' };

    // The CPL trend compares like with like: the blended CPL in the account's
    // dominant CURRENT currency against the same currency's previous CPL.
    const currency = dominant(blendedFold);
    const cplCurrent = cplIn(blendedFold, currency);
    const cplPrevious = cplIn(prevFold, currency);
    const cplChangePct =
      cplCurrent !== null && cplPrevious !== null && cplPrevious > 0
        ? r2(((cplCurrent - cplPrevious) / cplPrevious) * 100)
        : null;

    const spark = [...(sparkByAccount.get(account.id) ?? new Map()).entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, v]) => ({ date, leads: r2(v.leads), spend: r2(v.spend) }));

    const pacing = input.pacingByAccount?.get(account.id);
    const metaLead = buildMetaLeadCell(input.metaLeadsByAccount?.get(account.id));

    return {
      account,
      meta: metaCell,
      google: googleCell,
      blendedSpend: toAmounts(blendedFold.spendBy),
      blendedLeads: r2(blendedFold.leads),
      blendedCpl: cplAmounts(blendedFold),
      blendedVerdict,
      leadsPrevious: r2(prevFold.leads),
      cplCurrent: cplCurrent === null ? null : r2(cplCurrent),
      cplPrevious: cplPrevious === null ? null : r2(cplPrevious),
      cplChangePct,
      spark,
      pacingMeta: pacing?.meta ?? null,
      pacingGoogle: pacing?.google ?? null,
      health: input.healthByAccount.get(account.id) ?? 'ontrack',
      metaLead,
    };
  });
}

export type BookSortKey =
  | 'attention'
  | 'name'
  | 'metaSpend'
  | 'metaLeads'
  | 'metaCpl'
  // §11d — the lead-ACTION axis. Named `metaForm*` rather than `metaLead*` so
  // they cannot be misread as the lane's existing `metaLeads` (total_leads).
  | 'metaFormLeads'
  | 'metaFormCpl'
  | 'googleSpend'
  | 'googleLeads'
  | 'googleCpl'
  | 'blendedSpend'
  | 'blendedLeads'
  | 'blendedCpl'
  | 'cplChange'
  | 'pacing';

const HEALTH_ORDER: Record<HomeAccountHealth, number> = {
  critical: 0,
  attention: 1,
  ontrack: 2,
};

/**
 * A spending-no-leads row sorts WORSE than any finite CPL (the rule's ordering),
 * then by spend. ⚠️ Finite on purpose: Infinity - Infinity is NaN in the compare.
 */
const NO_LEADS_SORT_BASE = 1e15;
const cplSortValue = (v: CplVerdict, amounts: CurrencyAmount[] | null): number | null =>
  v.kind === 'no_leads' ? NO_LEADS_SORT_BASE + v.spend : sumAmounts(amounts);

/** Cross-currency SORT PROXY only — never a displayed figure. */
const sumAmounts = (a: CurrencyAmount[] | null): number | null =>
  a === null || a.length === 0 ? null : a.reduce((n, c) => n + c.amount, 0);

function sortValue(row: BookRow, key: BookSortKey): number | string | null {
  switch (key) {
    case 'attention':
      return HEALTH_ORDER[row.health];
    case 'name':
      return row.account.name.toLowerCase();
    case 'metaSpend':
      return sumAmounts(row.meta.spend);
    case 'metaLeads':
      return row.meta.active ? row.meta.leads : null;
    case 'metaCpl':
      return cplSortValue(row.meta.verdict, row.meta.cpl);
    // ⚠️ Null (no breakdown supplied, or no Meta rows) SINKS in both
    // directions — a "—" is not a zero.
    case 'metaFormLeads':
      return row.metaLead?.metaLeads ?? null;
    case 'metaFormCpl':
      return row.metaLead ? sumAmounts(row.metaLead.costPerMetaLead) : null;
    case 'googleSpend':
      return sumAmounts(row.google.spend);
    case 'googleLeads':
      return row.google.active ? row.google.leads : null;
    case 'googleCpl':
      return cplSortValue(row.google.verdict, row.google.cpl);
    case 'blendedSpend':
      return sumAmounts(row.blendedSpend);
    case 'blendedLeads':
      return row.blendedLeads;
    case 'blendedCpl':
      return cplSortValue(row.blendedVerdict, row.blendedCpl);
    case 'cplChange':
      return row.cplChangePct;
    case 'pacing': {
      const worst = [row.pacingMeta, row.pacingGoogle]
        .filter((p): p is NonNullable<typeof p> => p !== null)
        .map((p) => Math.abs(p.pace - 1));
      return worst.length > 0 ? Math.max(...worst) : null;
    }
  }
}

/**
 * Sort rows. Nulls sink to the end in both directions (a "—" is not a zero).
 * The DEFAULT ('attention' asc) is the morning order: needs-attention first,
 * then the worst CPL change within each health band.
 */
export function sortBookRows(
  rows: readonly BookRow[],
  key: BookSortKey,
  dir: 'asc' | 'desc',
): BookRow[] {
  const mul = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = sortValue(a, key);
    const bv = sortValue(b, key);
    if (av === null && bv === null) return tieBreak(a, b, key);
    if (av === null) return 1;
    if (bv === null) return -1;
    if (typeof av === 'string' || typeof bv === 'string') {
      const cmp = String(av).localeCompare(String(bv)) * mul;
      return cmp !== 0 ? cmp : tieBreak(a, b, key);
    }
    const cmp = (av - bv) * mul;
    return cmp !== 0 ? cmp : tieBreak(a, b, key);
  });
}

/** Within equal health, the worst CPL change floats up; then names. */
function tieBreak(a: BookRow, b: BookRow, key: BookSortKey): number {
  if (key === 'attention') {
    const ac = a.cplChangePct;
    const bc = b.cplChangePct;
    if (ac !== null || bc !== null) {
      if (ac === null) return 1;
      if (bc === null) return -1;
      if (ac !== bc) return bc - ac; // worst (most positive) first
    }
  }
  return a.account.name.localeCompare(b.account.name);
}
